/**
 * Pure helpers shared by ConversationThread, ContextRail and MessageComposer.
 */

export const SMS_CONSENT_TYPES = [
  { type: 'NOTIFICATION_SMS', label: 'Reminders' },
  { type: 'CAMPAIGN_SMS', label: 'Campaigns' },
  { type: 'FEEDBACK_SMS', label: 'Surveys' },
  { type: 'PAYMENT_SMS', label: 'Payments' },
];

const SOURCE_LABELS = {
  EHR_SYNC: 'from athena', MANUAL: 'set by staff', INTAKE_FORM: 'from intake', intake_form: 'from intake',
  verbal: 'verbal, by staff', PINPOINT_OPT_OUT: 'carrier STOP', TEXT_OPT_OUT: 'texted STOP', text_opt_out: 'texted STOP',
};

/**
 * Fold the per-type consent rows (patient_consents) and the legacy
 * patient_sms_consent badge payload into one view model for the rail.
 */
export function consentView(consents, legacy) {
  const list = Array.isArray(consents) ? consents : [];
  const byType = Object.fromEntries(list.map(c => [c.consent_type, c]));
  const legacyGranted = legacy?.consent_given === 1 && legacy?.consent_withdrawn !== 1;
  const rows = SMS_CONSENT_TYPES.map(({ type, label }) => {
    const c = byType[type];
    let state = 'unknown';
    if (c?.status === 'OPT_OUT') state = 'off';
    else if (c?.status === 'OPT_IN') state = 'on';
    else if (!c && legacyGranted && type === 'NOTIFICATION_SMS') state = 'on';
    const when = c?.updated_at ? shortDate(c.updated_at) : null;
    return { type, label, state, when, source: c?.source };
  });
  const known = rows.some(r => r.state !== 'unknown') || !!legacy;
  const offCount = rows.filter(r => r.state === 'off').length;
  const srcRow = rows.find(r => r.state === 'on' && r.source) || null;
  const source = srcRow ? (SOURCE_LABELS[srcRow.source] || srcRow.source) : (legacy?.consent_source ? (SOURCE_LABELS[legacy.consent_source] || legacy.consent_source) : null);
  return { rows, known, anyOff: offCount > 0, offCount, source, legacyGranted };
}

/** The next scheduled visit from a DESC-ordered appointment list, or null. */
export function nextAppointment(appointments) {
  if (!Array.isArray(appointments)) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const upcoming = appointments
    .filter(a => a && a.status === 'scheduled' && a.appointment_date)
    .map(a => ({ a, d: apptDate(a) }))
    .filter(x => x.d && x.d >= today)
    .sort((x, y) => x.d - y.d);
  return upcoming.length ? upcoming[0].a : null;
}

export function apptDate(a) {
  if (!a?.appointment_date) return null;
  const raw = String(a.appointment_date);
  let d;
  if (raw.includes('T') || raw.includes(' ')) d = new Date(raw);
  else {
    const [y, m, day] = raw.split('-').map(Number);
    d = new Date(y, (m || 1) - 1, day || 1);
    if (a.start_time) {
      const [hh, mm] = String(a.start_time).split(':').map(Number);
      if (!isNaN(hh)) d.setHours(hh, mm || 0, 0, 0);
    }
  }
  return isNaN(d.getTime()) ? null : d;
}

export function formatApptWhen(a) {
  const d = apptDate(a);
  if (!d) return a.appointment_date || '';
  const hasTime = !!a.start_time || String(a.appointment_date).includes('T');
  const date = d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
  return hasTime ? `${date} · ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : date;
}

export function shortDate(s) {
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

export function timeOf(s) {
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function dayLabel(s) {
  const date = new Date(s);
  const today = new Date();
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' });
}

export function humanize(s) {
  if (!s) return '';
  const t = String(s).replace(/_/g, ' ').trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

const ESCALATION_REASON_LABELS = {
  LOW_CONFIDENCE: 'low intent confidence',
  UNCLEAR_INTENT: 'unclear intent',
  CLINICAL_QUESTION: 'clinical question (PHI safeguard)',
  HUMAN_REQUESTED: 'patient asked for a human',
  CANCEL_REQUEST: 'cancellation request',
  COMPLAINT: 'patient complaint',
  NO_BOOKING_LINK: 'reschedule request, no booking link configured',
  AMBIGUOUS_INTENT: 'ambiguous intent',
  REFILL_REQUEST: 'refill request',
  RESULTS_REQUEST: 'results request',
  BILLING_QUESTION: 'billing question',
  QUESTION_NEEDS_HUMAN: 'question the AI could not answer',
};
export function escalationReason(reason) {
  if (!reason) return 'needs attention';
  return ESCALATION_REASON_LABELS[reason] || String(reason).toLowerCase().replace(/_/g, ' ');
}

/**
 * SMS segment maths. GSM-7 texts fit 160 chars in one part (153 per part when
 * concatenated); anything outside the GSM basic set forces UCS-2: 70 / 67.
 */
const GSM7 = '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM7_EXT = '^{}\\[~]|€';
export function smsSegments(text) {
  const s = text || '';
  let gsm = true, units = 0;
  for (const ch of s) {
    if (GSM7.includes(ch)) units += 1;
    else if (GSM7_EXT.includes(ch)) units += 2;
    else { gsm = false; break; }
  }
  if (!gsm) {
    const len = Array.from(s).length;
    return { encoding: 'UCS-2', chars: len, segments: len === 0 ? 0 : len <= 70 ? 1 : Math.ceil(len / 67) };
  }
  return { encoding: 'GSM-7', chars: units, segments: units === 0 ? 0 : units <= 160 ? 1 : Math.ceil(units / 153) };
}

export function formatPhone(p) {
  if (!p) return '';
  const digits = String(p).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  if (digits.length === 10) return `+1 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  return p;
}

export function initialsOf(first, last) {
  const f = first ? first.charAt(0).toUpperCase() : '';
  const l = last ? last.charAt(0).toUpperCase() : '';
  return f + l || '?';
}

// ---- thread segments ------------------------------------------------------

export function isClosedThread(sc) {
  return ['closed', 'resolved'].includes(String(sc?.status || '').toLowerCase());
}

export function threadTitle(sc) {
  const t = String(sc?.topic_type || '').toLowerCase();
  if (!t || t === 'general' || t === 'conversation') return 'Conversation';
  const channel = sc.channel && sc.channel !== 'sms' ? ` · ${humanize(sc.channel)}` : '';
  return `${humanize(t)}${channel}`;
}

export function dateRange(a, b) {
  const from = shortDate(a);
  const to = shortDate(b);
  if (!from) return to || '';
  if (!to || to === from) return from;
  return `${from} – ${to}`;
}

/**
 * Group a time-ordered ledger into one segment per sub-conversation so a
 * resolved thread can be folded to a single row. A message belongs to the
 * thread it was written in; items that carry no thread (automated reminders,
 * appointment confirmations) fall into whichever thread was open at the time.
 * Segments come back oldest first. A closed thread with nothing in it is
 * dropped; the active thread is always kept so it has a header to stand under.
 */
export function buildThreadSegments(timeline, subConversations, activeSubId = null) {
  const items = timeline || [];
  const subs = (subConversations || [])
    .filter(s => s && s.id != null)
    .slice()
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  if (!subs.length) {
    return items.length ? [describe({ id: 'all', sub: null, closed: false, items })] : [];
  }
  const byId = new Map(subs.map(s => [s.id, { id: s.id, sub: s, closed: isClosedThread(s) && s.id !== activeSubId, items: [] }]));
  const homeFor = (at) => {
    const t = new Date(at).getTime();
    let home = subs[0];
    for (const s of subs) {
      if (new Date(s.created_at).getTime() - 60000 <= t) home = s; else break;
    }
    return home.id;
  };
  for (const it of items) {
    const explicit = it.kind === 'message' ? it.message?.sub_conversation_id : it.subId;
    const id = explicit != null && byId.has(explicit) ? explicit : homeFor(it.at);
    byId.get(id).items.push(it);
  }
  return subs
    .map(s => byId.get(s.id))
    .filter(seg => seg.items.length || !seg.closed)
    .map(describe);
}

function describe(seg) {
  const msgs = seg.items.filter(it => it.kind === 'message' && !it.message?.event_type && !it.message?.is_notification);
  const firstAt = seg.items[0]?.at || seg.sub?.created_at || null;
  const lastAt = seg.items.length ? seg.items[seg.items.length - 1].at : (seg.sub?.resolved_at || seg.sub?.created_at || null);
  return {
    ...seg,
    title: seg.sub ? threadTitle(seg.sub) : 'Conversation',
    firstAt,
    lastAt,
    range: dateRange(firstAt, lastAt),
    messageCount: msgs.length,
    resolvedAt: seg.closed ? (seg.sub?.resolved_at || seg.sub?.updated_at || null) : null,
    resolvedBy: seg.closed ? (seg.sub?.resolved_by_name || null) : null,
    notes: seg.closed ? (seg.sub?.resolution_notes || null) : null,
  };
}

// ---- consent sources that are a carrier-level STOP ---------------------------

/**
 * Only these sources mean the patient (or their carrier) said STOP, which the
 * carrier enforces and nobody may override. Any other OPT_OUT row (athena's
 * consent-to-text flag via EHR_SYNC, a manual withdrawal) means "no consent on
 * file": staff see the consent-request path, not the hard block.
 * Mirrors HARD_STOP_SOURCES in src/controllers/SmsController.js.
 */
export const HARD_STOP_SOURCES = ['SMS_STOP', 'PINPOINT_OPT_OUT', 'SMS_KEYWORD', 'STOP_KEYWORD', 'CARRIER'];

export function isHardStop(consents, legacy) {
  const list = Array.isArray(consents) ? consents : [];
  // Legacy-only withdrawals predate typed consents and were written by the old STOP handler, so they stay a hard block.
  if (legacy?.consent_withdrawn === 1) return true;
  return list.some(c => c.consent_type === 'NOTIFICATION_SMS' && c.status === 'OPT_OUT' && HARD_STOP_SOURCES.includes(String(c.source || '').toUpperCase()));
}

// ---- composer templates ------------------------------------------------------

const PLACEHOLDER = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;

/**
 * Fill a template's {{placeholders}} from the thread. Keys the thread cannot
 * supply (intake_link, telehealth_link, …) are left in place so the composer
 * can refuse to send them to the patient as raw braces.
 */
export function renderTemplate(text, ctx = {}) {
  if (!text) return '';
  return String(text).replace(PLACEHOLDER, (whole, key) => {
    const v = ctx[key];
    return v === undefined || v === null || v === '' ? whole : String(v);
  });
}

export function unresolvedPlaceholders(text) {
  const out = [];
  String(text || '').replace(PLACEHOLDER, (whole, key) => { if (!out.includes(key)) out.push(key); return whole; });
  return out;
}

/** Build the placeholder context for the composer from what the thread already knows. */
export function templateContext({ patient, conversation, practice, appointment, bookingLink } = {}) {
  const first = patient?.first_name || conversation?.first_name || '';
  const last = patient?.last_name || conversation?.last_name || '';
  const when = appointment ? apptDate(appointment) : null;
  const ctx = {
    // Seeded templates and the Templates page use {{first_name}}; notification templates use {{patient_first_name}}.
    first_name: first,
    last_name: last,
    patient_first_name: first,
    patient_last_name: last,
    patient_name: [first, last].filter(Boolean).join(' '),
    appointment_type: appointment?.appointment_type || '',
    department_name: appointment?.department_name || '',
    practice_name: practice?.sms_brand_name || practice?.name || '',
    practice_phone: practice?.phone || practice?.sms_sender_phone || '',
    provider_name: appointment?.provider_name || '',
    appointment_date: when ? when.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }) : '',
    appointment_time: when ? when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '',
    booking_link: bookingLink || '',
    booking_url: bookingLink || '',
  };
  return ctx;
}
